import {
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import DateSelectBox from './DateSelectBox';
import moment from 'moment';
import { NumericFormat } from 'react-number-format';
import CurrencyFormat from '../../../../components/ui/text-formats/CurrencyFormat';

const SaleOfHomes = () => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const [total, setTotal] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedObject, setSelectedObject] = useState({});
  const [startAndFinishDate, setStartAndFinishDate] = useState({
    startDate: moment()
      .set('years', 2010)
      .startOf('years')
      .format('YYYY-MM-DD'),
    finishDate: moment().format('YYYY-MM-DD'),
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['saleOfHomes', startAndFinishDate],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/analytic/object/sale?from=${startAndFinishDate.startDate}&till=${startAndFinishDate.finishDate}`
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      if (data && data.length > 0) {
        setTotal(data.reduce((acc, curr) => acc + curr?.count, 0));
      }
    },
    enabled: !hasError,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  return (
    <div className='residential-complex-item sale-of-homes !shadow-md !base-border'>
      <div className='residential-complex-item-header'>
        <div className='residential-complex-item-title sale-of-homes-title'>
          {t('boss.residentialComplex.saleHouse')}
        </div>
        <div className='w-[150px]'>
          <DateSelectBox
            setStartAndFinishDate={setStartAndFinishDate}
            disabled={isLoading || isFetching}
          />
        </div>
      </div>

      <div className='sale-of-homes-body'>
        <ButtonBase className='total-sales'>
          {isLoading || isFetching ? (
            <div className='flex mr-2'>
              <CircularProgress size={20} color='inherit' />
            </div>
          ) : (
            <NumericFormat
              value={total}
              displayType={'text'}
              allowNegative={false}
              thousandSeparator={' '}
              decimalScale={3}
              className='sales-number bg-transparent'
            />
          )}{' '}
          {t('boss.residentialComplex.soldHomes')}
        </ButtonBase>
        <div className='sales-wrapper'>
          {isLoading || isFetching ? (
            <div className='flex items-center justify-center h-[200px]'>
              <CircularProgress size={30} color='inherit' />
            </div>
          ) : data && data.length > 0 ? (
            data.map((item, index) => (
              <div key={`sale-item-${index}`} className='sale-item'>
                <i className='bi bi-buildings text-2xl mr-1' />
                <div className='object-name'>{item?.name}</div>
                <span className='divider'>|</span>
                <div className='sale-count'>
                  {t('common.global.soldHomes')}: <span>{item?.count}</span>
                </div>
                <Button
                  variant='action'
                  color='secondary'
                  onClick={() => {
                    setSelectedObject(item);
                    setOpenModal(true);
                  }}
                >
                  <i className='bi bi-chevron-right' />
                </Button>
              </div>
            ))
          ) : (
            <div className='no-data-found-wrapper h-[100px]'>
              <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
              {t('common.global.noDataFound')}
            </div>
          )}
        </div>
      </div>

      <SaleOfHomesByObject
        open={openModal}
        setOpen={setOpenModal}
        object={selectedObject}
        startAndFinishDate={startAndFinishDate}
      />
    </div>
  );
};

const SaleOfHomesByObject = ({
  open,
  setOpen,
  object = {},
  startAndFinishDate = {},
}) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [totalDetails, setTotalDetails] = useState({});

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['saleOfHomesByObject', object],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/analytic/object/blocks/${object?.id}?from=${startAndFinishDate.startDate}&till=${startAndFinishDate.finishDate}`
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      if (data && data.length > 0) {
        let newTotalDetails = {
          insale: 0,
          order: 0,
          contract: 0,
          repaired: 0,
          norepaired: 0,
          left_square: 0,
          sold_square: 0,
          square: 0,
          left: { sum: 0, valute: 0 },
          paid: { sum: 0, valute: 0 },
          monthly_income: { sum: 0, valute: 0 },
          avg_price_m2: { sum: 0, valute: 0 },
          contract_types: [],
        };
        const allCtrTypes = data?.flatMap((obj) => obj?.contract_types || []);

        const mergedMap = new Map();

        allCtrTypes.forEach((item) => {
          const existing = mergedMap.get(item.name);
          if (existing) {
            existing.total += item.total;
          } else {
            mergedMap.set(item.name, { name: item.name, total: item.total });
          }
        });

        const contract_types = Array.from(mergedMap.values());

        const totalSum = data?.reduce((acc, item) => {
          return (
            acc + item?.avg_price_m2?.sum * (item?.square + item?.left_square)
          );
        }, 0);

        const totalVal = data?.reduce((acc, item) => {
          return (
            acc +
            item?.avg_price_m2?.valute * (item?.square + item?.left_square)
          );
        }, 0);

        const square = data?.reduce((acc, item) => {
          return acc + item?.square + item?.left_square;
        }, 0);

        const left = data?.reduce(
          (acc, item) => {
            acc.sum += item?.left?.sum;
            acc.valute += item?.left?.valute;
            return acc;
          },
          { sum: 0, valute: 0 }
        );

        const paid = data?.reduce(
          (acc, item) => {
            acc.sum += item?.paid?.sum;
            acc.valute += item?.paid?.valute;
            return acc;
          },
          { sum: 0, valute: 0 }
        );

        const monthly_income = data?.reduce(
          (acc, item) => {
            acc.sum += item?.monthly_income?.sum;
            acc.valute += item?.monthly_income?.valute;
            return acc;
          },
          { sum: 0, valute: 0 }
        );

        data?.forEach((item) => {
          newTotalDetails.insale += item?.insale;
          newTotalDetails.order += item?.order;
          newTotalDetails.contract += item?.contract;
          newTotalDetails.repaired += item?.repaired;
          newTotalDetails.norepaired += item?.norepaired;
          newTotalDetails.left_square += item?.left_square;
          newTotalDetails.sold_square += item?.square;
        });
        newTotalDetails.left = left;
        newTotalDetails.paid = paid;
        newTotalDetails.monthly_income = monthly_income;
        newTotalDetails.square = square;
        newTotalDetails.contract_types = contract_types;
        newTotalDetails.avg_price_m2 = {
          sum: totalSum / square,
          valute: totalVal / square,
        };
        setTotalDetails(newTotalDetails);
      }
    },
    enabled: !!object?.id,
    retry: false,
  });

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby='sale-of-homes-by-object'
      aria-describedby='sale-of-homes-by-object-modal'
      fullWidth
      maxWidth='xl'
      PaperProps={{ className: '!shadow-md !base-border' }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='sale-of-homes-by-object'
        className='text-center bg-gray-100 !mb-2'
      >
        <span> {t('boss.residentialComplex.saleHouse')}</span>
      </DialogTitle>

      <DialogContent>
        <div className='sale-of-homes-by-object-modal-wrapper'>
          {isLoading || isFetching ? (
            <div className='w-full h-[100px] flex items-center justify-center'>
              <CircularProgress size={30} color='inherit' />
            </div>
          ) : data && data.length > 0 ? (
            <div>
              <div className='header-details'>
                <div className='header-details-item all-homes'>
                  <div className='object-name'>
                    <i className='bi bi-buildings text-2xl mr-1' />
                    {object?.name}
                  </div>
                  <div className='homes-number total-homes'>
                    <div className='label'>
                      {t('boss.residentialComplex.allHomes')}
                    </div>
                    <div className='value'>
                      {totalDetails?.insale +
                        totalDetails?.order +
                        totalDetails?.contract}
                    </div>
                  </div>
                </div>
                <div className='header-details-item ordered-homes homes-number'>
                  <div className='label text-green-900 font-semibold whitespace-nowrap'>
                    {t('boss.residentialComplex.inactiveOrdered')}
                  </div>
                  <div className='value'>{totalDetails?.order}</div>
                </div>
                <div className='header-details-item in-sale-homes'>
                  <div className='homes-number'>
                    <div className='label text-green-900 font-semibold'>
                      {t('boss.residentialComplex.onSale')}
                    </div>
                    <div className='value'>{totalDetails?.insale}</div>
                  </div>
                  <div className='divider' />
                  <div className='homes-by-repair'>
                    {!!totalDetails?.left_square && (
                      <div className='homes-by-repair-item flex-col repaired whitespace-nowrap'>
                        <div className='label'>
                          {t('common.table.square')}:{' '}
                        </div>
                        <CurrencyFormat
                          value={totalDetails?.left_square}
                          suffix={' m²'}
                          decimalScale={2}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className='header-details-item in-sale-homes'>
                  <div className='homes-number'>
                    <div className='label text-green-900 font-semibold'>
                      {t('common.global.soldHomes')}
                    </div>
                    <div className='value'>{totalDetails?.contract}</div>
                  </div>
                  <div className='divider' />
                  <div className='homes-by-repair'>
                    {totalDetails?.contract_types?.map((item) => (
                      <div
                        key={item?.name}
                        className='homes-by-repair-item repaired'
                      >
                        {item?.name}: <span>{item?.total}</span>
                      </div>
                    ))}
                  </div>
                  <div className='divider' />
                  <div className='homes-by-repair'>
                    <div className='homes-by-repair-item repaired'>
                      {t('common.typeList.repairType.repaired')}:{' '}
                      <span>{totalDetails?.repaired}</span>
                    </div>
                    <div className='homes-by-repair-item no-repaired'>
                      {t('common.typeList.repairType.notRepaired')}:{' '}
                      <span>{totalDetails?.norepaired}</span>
                    </div>
                  </div>
                  <div className='divider' />
                  <div className='homes-by-repair'>
                    <div className='homes-by-repair-item repaired whitespace-nowrap'>
                      {t('common.table.square')}:{' '}
                      <CurrencyFormat
                        value={totalDetails?.sold_square}
                        suffix={' m²'}
                        decimalScale={2}
                      />
                    </div>
                    {!!totalDetails?.paid?.sum && (
                      <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                        UZS:{' '}
                        <CurrencyFormat
                          value={totalDetails?.paid?.sum}
                          decimalScale={2}
                          suffix={' UZS'}
                        />
                      </div>
                    )}
                    {!!totalDetails?.paid?.valute && (
                      <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                        USD:{' '}
                        <CurrencyFormat
                          value={totalDetails?.paid?.valute}
                          decimalScale={2}
                          suffix={' USD'}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className='header-details-item in-sale-homes'>
                  <div className='homes-by-repair'>
                    <div className='homes-by-repair-item repaired whitespace-nowrap font-semibold text-green-900'>
                      <Trans
                        i18nKey={'boss.residentialComplex.monthlyIncome'}
                      />
                      :
                    </div>
                    {!!totalDetails?.monthly_income?.sum && (
                      <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                        UZS:{' '}
                        <CurrencyFormat
                          value={totalDetails?.monthly_income?.sum}
                          decimalScale={2}
                          suffix={' UZS'}
                        />
                      </div>
                    )}
                    {!!totalDetails?.monthly_income?.valute && (
                      <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                        USD:{' '}
                        <CurrencyFormat
                          value={totalDetails?.monthly_income?.valute}
                          decimalScale={2}
                          suffix={' USD'}
                        />
                      </div>
                    )}
                  </div>
                  <div className='divider' />
                  <div className='homes-by-repair'>
                    <div className='homes-by-repair-item repaired whitespace-nowrap font-semibold text-green-900'>
                      <Trans i18nKey={'common.table.leftSum'} />:
                    </div>
                    {!!totalDetails?.left?.sum && (
                      <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                        UZS:{' '}
                        <CurrencyFormat
                          value={totalDetails?.left?.sum}
                          decimalScale={2}
                          suffix={' UZS'}
                        />
                      </div>
                    )}
                    {!!totalDetails?.left?.valute && (
                      <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                        USD:{' '}
                        <CurrencyFormat
                          value={totalDetails?.left?.valute}
                          decimalScale={2}
                          suffix={' USD'}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className='body-details'>
                <Grid
                  container
                  spacing={2}
                  columns={{ xs: 12, sm: 12, md: 12 }}
                >
                  {data.map((item, index) => (
                    <Grid
                      item={true}
                      md={12}
                      sm={12}
                      xs={12}
                      key={`block-sale-homes-${index}`}
                    >
                      <div className='block-sale-homes-item !shadow-md !base-border'>
                        <div className='item-header'>
                          <div className='block-name'>
                            <i className='bi bi-building text-xl mr-1' />
                            {item?.name}
                          </div>
                          <div className='item-total-homes'>
                            {t('boss.residentialComplex.allHomes')}:{' '}
                            <span>
                              {item?.insale + item?.order + item?.contract}
                            </span>
                          </div>
                        </div>
                        <div className='item-body !gap-1 justify-between'>
                          <div className='item-sale-homes !px-2 !gap-2 !m-0 ordered-homes homes-number flex-grow'>
                            <div className='label text-indigo-700 font-semibold'>
                              {t('boss.residentialComplex.inactiveOrdered')}
                            </div>
                            <div className='value'>{item?.order}</div>
                          </div>
                          <div className='item-sale-homes !px-2 !gap-2 !m-0 in-sale-homes flex-grow'>
                            <div className='homes-number'>
                              <div className='label whitespace-nowrap text-indigo-700 font-semibold'>
                                {t('boss.residentialComplex.onSale')}
                              </div>
                              <div className='value'>{item?.insale}</div>
                            </div>
                            <div className='divider !mx-0' />
                            <div className='homes-by-repair'>
                              <div className='homes-by-repair-item flex-col repaired whitespace-nowrap'>
                                <div className='label'>
                                  {t('common.table.square')}:{' '}
                                </div>
                                <CurrencyFormat
                                  value={item?.left_square}
                                  suffix={' m²'}
                                  decimalScale={2}
                                />
                              </div>
                            </div>
                          </div>
                          <div className='item-sale-homes !px-2 !gap-2 !m-0 in-sale-homes flex-grow'>
                            <div className='homes-number'>
                              <div className='label whitespace-nowrap text-indigo-700 font-semibold'>
                                {t('common.global.soldHomes')}
                              </div>
                              <div className='value'>{item?.contract}</div>
                            </div>
                            <div className='divider !mx-0' />
                            <div className='homes-by-repair'>
                              {item?.contract_types?.map((item) => (
                                <div
                                  key={item?.name}
                                  className='homes-by-repair-item repaired'
                                >
                                  {item?.name}: <span>{item?.total}</span>
                                </div>
                              ))}
                            </div>

                            <div className='divider !mx-0' />
                            <div className='homes-by-repair'>
                              <div className='homes-by-repair-item repaired'>
                                {t('common.typeList.repairType.repaired')}:{' '}
                                <span>{item?.repaired}</span>
                              </div>
                              <div className='homes-by-repair-item no-repaired'>
                                {t('common.typeList.repairType.notRepaired')}:{' '}
                                <span>{item?.norepaired}</span>
                              </div>
                            </div>
                            <div className='divider !mx-0' />
                            <div className='homes-by-repair'>
                              <div className='homes-by-repair-item repaired whitespace-nowrap'>
                                {t('common.table.square')}:{' '}
                                <CurrencyFormat
                                  value={item?.square}
                                  suffix={' m²'}
                                  decimalScale={2}
                                />
                              </div>
                              {!!item?.paid?.sum && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  UZS:{' '}
                                  <CurrencyFormat
                                    value={item?.paid?.sum}
                                    decimalScale={2}
                                    suffix={' UZS'}
                                  />
                                </div>
                              )}
                              {!!item?.paid?.valute && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  USD:{' '}
                                  <CurrencyFormat
                                    value={item?.paid?.valute}
                                    decimalScale={2}
                                    suffix={' USD'}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className='item-sale-homes !px-2 !gap-2 !m-0 in-sale-homes flex-grow'>
                            <div className='homes-by-repair'>
                              <div className='homes-by-repair-item repaired whitespace-nowrap font-semibold text-indigo-500'>
                                <Trans
                                  i18nKey={
                                    'boss.residentialComplex.averagePriceSquareShort'
                                  }
                                >
                                  1 m<sup>2</sup> o'rtacha narxi
                                </Trans>
                                :
                              </div>
                              {!!item?.avg_price_m2?.sum && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  UZS:{' '}
                                  <CurrencyFormat
                                    value={item?.avg_price_m2?.sum}
                                    decimalScale={2}
                                    suffix={' UZS'}
                                  />
                                </div>
                              )}
                              {!!item?.avg_price_m2?.valute && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  USD:{' '}
                                  <CurrencyFormat
                                    value={item?.avg_price_m2?.valute}
                                    decimalScale={2}
                                    suffix={' USD'}
                                  />
                                </div>
                              )}
                            </div>
                            <div className='divider !mx-0' />
                            <div className='homes-by-repair'>
                              <div className='homes-by-repair-item repaired whitespace-nowrap font-semibold text-indigo-500'>
                                <Trans
                                  i18nKey={
                                    'boss.residentialComplex.monthlyIncome'
                                  }
                                />
                                :
                              </div>
                              {!!item?.monthly_income?.sum && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  UZS:{' '}
                                  <CurrencyFormat
                                    value={item?.monthly_income?.sum}
                                    decimalScale={2}
                                    suffix={' UZS'}
                                  />
                                </div>
                              )}
                              {!!item?.monthly_income?.valute && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  USD:{' '}
                                  <CurrencyFormat
                                    value={item?.monthly_income?.valute}
                                    decimalScale={2}
                                    suffix={' USD'}
                                  />
                                </div>
                              )}
                            </div>
                            <div className='divider !mx-0' />
                            <div className='homes-by-repair'>
                              <div className='homes-by-repair-item repaired whitespace-nowrap font-semibold text-indigo-500'>
                                <Trans
                                  i18nKey={
                                    'boss.residentialComplex.priceOfHomes'
                                  }
                                />
                                :
                              </div>
                              {!!item?.total_sum?.sum && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  UZS:{' '}
                                  <CurrencyFormat
                                    value={item?.total_sum?.sum}
                                    decimalScale={2}
                                    suffix={' UZS'}
                                  />
                                </div>
                              )}
                              {!!item?.total_sum?.valute && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  USD:{' '}
                                  <CurrencyFormat
                                    value={item?.total_sum?.valute}
                                    decimalScale={2}
                                    suffix={' USD'}
                                  />
                                </div>
                              )}
                            </div>
                            <div className='divider !mx-0' />
                            <div className='homes-by-repair'>
                              <div className='homes-by-repair-item repaired whitespace-nowrap font-semibold text-indigo-500'>
                                <Trans i18nKey={'common.table.leftSum'} />:
                              </div>
                              {!!item?.left?.sum && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  UZS:{' '}
                                  <CurrencyFormat
                                    value={item?.left?.sum}
                                    decimalScale={2}
                                    suffix={' UZS'}
                                  />
                                </div>
                              )}
                              {!!item?.left?.valute && (
                                <div className='homes-by-repair-item no-repaired whitespace-nowrap'>
                                  USD:{' '}
                                  <CurrencyFormat
                                    value={item?.left?.valute}
                                    decimalScale={2}
                                    suffix={' USD'}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </div>
            </div>
          ) : (
            <div className='no-data-found-wrapper w-full h-[100px]'>
              <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
              {t('common.global.noDataFound')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaleOfHomes;
